import { Component, h, Prop, State } from '@stencil/core';
import { Cart, OrderDetails, Translations, Variation, VariationControls, VariationItem } from '../../redux/types';
import { store, Unsubscribe } from '@stencil/redux';
import { postVariationsForItem } from '../../api';
import { addItemToCart, fetchItemsForCurrentStep, removeItemFromCart } from '../../redux/actions';
import { trans } from '../../utils/trans';
import { ComponentView } from '../../types/types';

@Component({
  tag: 'rentware-variation-selection',
  shadow: true,
  styleUrl: 'style.sass',
})
export class RentwareVariationSelection {
  @Prop() itemId: string;
  @Prop() isWaiting: boolean;
  @Prop() waitingCallback;
  @Prop() component: ComponentView;
  @Prop() locale: string;
  @Prop() translations: Translations;
  @Prop() bookingParams = null; // params used when no order is present

  @Prop({ mutable: true }) orderDetailsState: OrderDetails;
  @Prop({ mutable: true }) cartState: Cart;

  @State() variation: Variation;
  @State() amountForId: { [id: string]: number } = {};
  @State() waitingArticles: string[] = [];
  @State() selectedVariationId: string;

  unsubscribe!: Unsubscribe;
  addItemToCart!: typeof addItemToCart;
  removeItemFromCart!: typeof removeItemFromCart;
  fetchItemsForStep!: typeof fetchItemsForCurrentStep;

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, state => {
      postVariationsForItem(
        this.itemId,
        state.orderDetails?.id,
        state.app.locale,
        data => {
          this.variation = data;
        },
        undefined,
      );

      return {
        cartState: state.cart,
        locale: state.app.locale,
        translations: state.system.translations,
        orderDetailsState: state.orderDetails,
      };
    });

    store.mapDispatchToProps(this, {
      addItemToCart,
      removeItemFromCart,
      fetchItemsForStep: fetchItemsForCurrentStep,
    });
  }

  componentDidLoad() {
    if (!!this.variationsInCart && this.variationsInCart.length > 0) {
      this.selectedVariationId = this.variationsInCart[0]?.id;
    }
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  handleChangeSelectedVariation = id => {
    this.selectedVariationId = id;
  };

  blockArticle = id => {
    this.waitingArticles = [...this.waitingArticles, id];
    if (!!this.waitingCallback) this.waitingCallback(true);
  };

  freeArticle = id => {
    this.waitingArticles = this.waitingArticles.filter(item => item !== id);
    if (!!this.waitingCallback) this.waitingCallback(false);
  };

  handleChangeAmount = (id, idInCart, newAmount, oldAmount) => {
    if (newAmount >= 0 && newAmount < 1000) {
      const difference = newAmount - oldAmount;
      this.blockArticle(id);

      const fetchCallback = () =>
        this.fetchItemsForStep(() =>
          postVariationsForItem(
            this.itemId,
            this.orderDetailsState?.id,
            this.locale,
            data => {
              this.variation = data;
              this.freeArticle(id);
            },
            undefined,
          ),
        );

      if (difference > 0) {
        const variations = new Array(difference)?.fill(id);

        this.addItemToCart(
          {
            itemId: this.itemId,
            amountToAdd: difference,
            variationsIds: variations,
            dateRange: !!this.bookingParams
              ? {
                  from: this.bookingParams.fromDate,
                  to: this.bookingParams.toDate,
                }
              : null,
            people: this.bookingParams?.people,
          },
          fetchCallback,
          fetchCallback,
        );
      } else if (difference < 0) {
        const variations = new Array(-difference)?.fill(id);

        this.removeItemFromCart({ itemId: idInCart, amountToRemove: -difference, variationsIds: variations }, fetchCallback, fetchCallback);
      } else if (difference === 0) {
        const variations = new Array(newAmount)?.fill(id);

        this.removeItemFromCart({ itemId: idInCart, amountToRemove: oldAmount, variationsIds: variations }, fetchCallback, fetchCallback);
      }
    }
  };

  get selectedText() {
    return trans('articleBooking.selected', this.locale, this.translations);
  }

  get unselectedText() {
    return trans('articleBooking.add', this.locale, this.translations);
  }

  get itemInCart() {
    return this.cartState?.items?.find(item => item.itemId === this.itemId) || null;
  }

  get variationsInCart() {
    const productsInItem = this.itemInCart?.products || [];
    const variationsInCart = productsInItem.find(item => !!item.variation)?.variation;

    return variationsInCart?.items;
  }

  makeControlValues = (item: VariationItem): VariationControls => {
    return {
      disabled: this.waitingArticles?.includes(item.id),
      id: item.id,
      minAmount: 1,
      maxAmount: item?.quantityInCart + item?.quantity,
      itemAmount: item?.quantityInCart,
      changeAmountCallback: (newAmount, oldAmount) => this.handleChangeAmount(item.id, this.itemInCart?.id, newAmount, oldAmount),
      deleteArticleCallback: () =>
        this.handleChangeAmount(
          item.id,
          this.itemInCart?.id,
          this.variationsInCart.find(cartItem => cartItem.id === item.id)?.quantity || 0,
          this.variationsInCart.find(cartItem => cartItem.id === item.id)?.quantity || 0,
        ),
    };
  };

  render() {
    if (!this.variation || !this.itemInCart) {
      return null;
    }

    if (this.component === ComponentView.Checkout) {
      return (
        <rentware-variation-selection-details-template
          variation={this.variation}
          variationsInCart={this.variationsInCart}
          itemInCart={this.itemInCart}
          waitingArticles={this.waitingArticles}
          handleChangeAmount={this.handleChangeAmount}
          selectedText={this.selectedText}
          unselectedText={this.unselectedText}
          selectedVariationId={this.selectedVariationId}
          changeSelectedVariation={this.handleChangeSelectedVariation}
          isWaiting={this.isWaiting}
        />
      );
    }

    if (this.component === ComponentView.Details) {
      return (
        <rentware-variation-selection-details-template
          variation={this.variation}
          variationsInCart={this.variationsInCart}
          itemInCart={this.itemInCart}
          waitingArticles={this.waitingArticles}
          handleChangeAmount={this.handleChangeAmount}
          selectedText={this.selectedText}
          unselectedText={this.unselectedText}
          selectedVariationId={this.selectedVariationId}
          changeSelectedVariation={this.handleChangeSelectedVariation}
          isWaiting={this.isWaiting}
        />
      );
    }

    if (this.component === ComponentView.Item) {
      return (
        <rentware-variation-selection-item-template
          variation={this.variation}
          variationsInCart={this.variationsInCart}
          itemInCart={this.itemInCart}
          waitingArticles={this.waitingArticles}
          handleChangeAmount={this.handleChangeAmount}
          selectedText={this.selectedText}
          unselectedText={this.unselectedText}
          selectedVariationId={this.selectedVariationId}
          changeSelectedVariation={this.handleChangeSelectedVariation}
          isWaiting={this.isWaiting}
        />
      );
    }

    const itemsWithControls = this.variation.items.map(varItem => {
      return { ...varItem, controlValues: this.makeControlValues(varItem) };
    });

    return <rentware-variation-selection-cart-template variationItems={itemsWithControls} />;
  }
}
